import type { BodyCompositionOut } from '../../api/types'
import { useUnits } from '../../lib/UnitsProvider'
import { AsyncSection } from '../ui/AsyncPanel'
import { Panel, Section, TOKENS } from '../ui/dashboard-primitives'
import { useScanPanel } from './panelStates'

interface Metric {
  key: string
  value: string
  delta: number | null
  betterWhenHigher: boolean
  note?: string
  /**
   * Converts `Math.abs(delta)` (which is always a raw kilogram figure --
   * `d()` below never converts) into the user's body-weight unit for
   * display. Present only on the four mass tiles (Weight/Muscle/Fat/
   * Visceral); Cell fitness and Bio age omit it and `DeltaLine` falls back to
   * the bare number, because those two carry no unit at all and converting
   * them would be wrong, not just unnecessary.
   */
  formatDeltaMagnitude?: (absKg: number) => string
}

function fmt(v: number | null, digits = 1, unit = ''): string {
  return v == null ? '·' : `${v.toFixed(digits)}${unit}`
}

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(a).getTime() - new Date(b).getTime()) / 86_400_000)
}

/**
 * `formatBodyWeight`/`signedBodyWeight` threaded in as parameters rather than
 * imported raw: this is a plain function, not a component, so it can't call
 * `useUnits()` itself — same shape as `heroStats.ts`.
 */
function buildMetrics(
  latest: BodyCompositionOut,
  formatBodyWeight: (kg: number | null | undefined) => string,
  signedBodyWeight: (kg: number | null | undefined) => string,
  prior?: BodyCompositionOut,
): Metric[] {
  const d = (n: keyof BodyCompositionOut) => {
    const cur = latest[n] as number | null
    const prev = prior?.[n] as number | null | undefined
    return cur != null && prev != null ? Number((cur - prev).toFixed(1)) : null
  }
  // `d()` above always yields a raw kilogram figure -- `weight_kg`,
  // `muscle_total_kg`, `fat_total_kg` and `visceral_fat_kg` are all masses.
  // `signedBodyWeight` already carries R10's stone-delta rule (whole signed
  // pounds, not a "+0 st 5 lb" compound), so reuse it for the magnitude and
  // drop its leading sign -- `DeltaLine`'s own arrow/color already say
  // direction, and re-deriving the stone-delta math here would just be a
  // second, driftable copy of R10.
  const massDeltaMagnitude = (absKg: number) => signedBodyWeight(absKg).replace(/^\+/, '')
  return [
    {
      key: 'Weight',
      value: formatBodyWeight(latest.weight_kg),
      delta: d('weight_kg'),
      betterWhenHigher: false,
      formatDeltaMagnitude: massDeltaMagnitude,
    },
    {
      key: 'Muscle',
      value: formatBodyWeight(latest.muscle_total_kg),
      delta: d('muscle_total_kg'),
      betterWhenHigher: true,
      note: latest.muscle_pct_of_body != null ? `${latest.muscle_pct_of_body.toFixed(1)}% of body` : undefined,
      formatDeltaMagnitude: massDeltaMagnitude,
    },
    {
      key: 'Fat',
      value: formatBodyWeight(latest.fat_total_kg),
      delta: d('fat_total_kg'),
      betterWhenHigher: false,
      note: latest.fat_pct_of_body != null ? `${latest.fat_pct_of_body.toFixed(1)}% of body` : undefined,
      formatDeltaMagnitude: massDeltaMagnitude,
    },
    {
      key: 'Visceral',
      value: formatBodyWeight(latest.visceral_fat_kg),
      delta: d('visceral_fat_kg'),
      betterWhenHigher: false,
      formatDeltaMagnitude: massDeltaMagnitude,
    },
    // Cell fitness and Bio age are not masses -- no `formatDeltaMagnitude`,
    // so `DeltaLine` renders their raw delta unconverted, same as before.
    { key: 'Cell fitness', value: fmt(latest.cell_fitness ?? null, 1), delta: d('cell_fitness'), betterWhenHigher: true },
    { key: 'Bio age', value: latest.bioage != null ? `${Math.round(latest.bioage)} yrs` : '·', delta: d('bioage'), betterWhenHigher: false },
  ]
}

function DeltaLine({ m }: { m: Metric }) {
  if (m.delta == null || m.delta === 0) {
    return (
      <p className="mt-1 text-[0.625rem] tracking-[0.1em]" style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}>
        {m.note ?? 'no change'}
      </p>
    )
  }
  const good = m.betterWhenHigher === m.delta > 0
  const arrow = m.delta > 0 ? '▲' : '▼'
  const magnitude = m.formatDeltaMagnitude ? m.formatDeltaMagnitude(Math.abs(m.delta)) : Math.abs(m.delta)
  return (
    <p
      className="mt-1 text-[0.625rem] tracking-[0.1em]"
      style={{ fontFamily: 'var(--font-mono)', color: good ? TOKENS.amber : TOKENS.terracotta }}
    >
      {arrow} {magnitude}
      {m.note ? ` · ${m.note}` : ''}
    </p>
  )
}

const HEAD = { index: '03', eyebrow: 'InBody · body composition', title: 'Body composition' }

/**
 * Section 03, body composition.
 *
 * A panel whose "nothing" hides a whole section, heading and all, so its states
 * go through `AsyncSection` rather than an `AsyncPanel` slot inside the section
 * (#214): hidden while loading and when there are no scans, as it always has
 * been, because a skeleton would flash and vanish for every user without a
 * scan; the section with the house error when the request failed, which used to
 * hide it too and made an outage invisible (#170).
 */
export function ScanStrip() {
  const scan = useScanPanel()
  return (
    <AsyncSection
      head={HEAD}
      sources={scan.sources}
      hasContent={scan.hasContent}
      subject="Your body composition"
    >
      {() => scan.data && <ScanSection data={scan.data} />}
    </AsyncSection>
  )
}

function ScanSection({ data }: { data: BodyCompositionOut[] }) {
  const { formatBodyWeight, signedBodyWeight } = useUnits()
  const latest = data[data.length - 1]
  const prior = data.length > 1 ? data[data.length - 2] : undefined
  const metrics = buildMetrics(latest, formatBodyWeight, signedBodyWeight, prior)

  const gap = prior ? daysBetween(latest.date, prior.date) : null
  const muscle = metrics[1].delta
  const fat = metrics[2].delta
  // Same body-mass deltas the "Muscle"/"Fat" tiles above already render in
  // the user's unit -- this subtitle used to hardcode " kg" regardless, which
  // would have read "Muscle +2.2 kg" directly under a tile reading "st" the
  // moment a UK-style user picked stone. Beyond the brief's literal four
  // `fmt()` calls, but the same body-weight quantity as one of them; see the
  // task report for why it's in scope.
  const subtitle =
    prior && gap
      ? `${gap} days since the last scan.` +
        (muscle != null ? ` Muscle ${signedBodyWeight(muscle)},` : '') +
        (fat != null ? ` fat ${signedBodyWeight(fat)}.` : '')
      : 'Your most recent InBody scan.'

  return (
    <Section {...HEAD} subtitle={subtitle}>
      <Panel variant="light" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-7 mt-4">
        {metrics.map((m) => (
          <div key={m.key}>
            <span
              className="text-[0.5625rem] tracking-[0.24em] uppercase"
              style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamFaint }}
            >
              {m.key}
            </span>
            <p
              className="mt-2 tabular-nums text-[1.0625rem]"
              style={{ fontFamily: 'var(--font-mono)', color: TOKENS.cream }}
            >
              {m.value}
            </p>
            <DeltaLine m={m} />
          </div>
        ))}
      </Panel>
    </Section>
  )
}
